/** Pure rules behind the energy visualisations. No I/O, no formatting. */
import type { Connection, EnergyBucket, EnergyHistory, HistoryRange, LiveReading, PhaseReading } from './model';

/** Below this the house is "about in balance": the meter's own resolution and standby noise. */
export const NEUTRAL_BAND_W = 50;

export type PowerDirection = 'import' | 'export' | 'neutral';

export function powerDirection(netW: number): PowerDirection {
  if (Math.abs(netW) < NEUTRAL_BAND_W) return 'neutral';
  return netW > 0 ? 'import' : 'export';
}

/** Share of the connection limit from which a phase is "high" and "at the limit". */
export const PHASE_HIGH_RATIO = 0.8;
export const PHASE_LIMIT_RATIO = 0.92;

export type PhaseLevel = 'normal' | 'high' | 'limit';

export interface PhaseLoad {
  /** 0…1 for the bar; can exceed 1 when the main fuse is being overloaded. */
  ratio: number;
  level: PhaseLevel;
}

/** For 3×25 A: high from 20 A, at the limit from 23 A. */
export function phaseLoad(currentA: number, maxAmps: number): PhaseLoad {
  const ratio = maxAmps > 0 ? Math.abs(currentA) / maxAmps : 0;
  const level: PhaseLevel = ratio >= PHASE_LIMIT_RATIO ? 'limit' : ratio >= PHASE_HIGH_RATIO ? 'high' : 'normal';
  return { ratio, level };
}

/** Largest minus smallest phase current; null when fewer than two phases report. */
export function phaseImbalanceA(phases: PhaseReading[]): number | null {
  const amps = phases.map((p) => p.currentA).filter((a): a is number => a !== null);
  if (amps.length < 2) return null;
  return Math.max(...amps) - Math.min(...amps);
}

export const NOMINAL_VOLTAGE = 230;

/** Dutch low-voltage band (EN 50160: 230 V ±10 %). */
export function voltageInBand(voltageV: number): boolean {
  return voltageV >= 207 && voltageV <= 253;
}

/**
 * Older meters (DSMR 2.2) report neither current nor voltage. On a
 * single-phase connection the current is then estimated from the net power
 * at nominal voltage and marked as an estimate; on three phases it cannot be
 * split, so the phases stay empty.
 */
export function withEstimatedCurrent(reading: LiveReading, connection: Connection): PhaseReading[] {
  if (reading.phases.some((p) => p.currentA !== null)) return reading.phases;
  if (connection.phases !== 1) return reading.phases;
  const voltageV = reading.phases[0]?.voltageV ?? null;
  return [{ phase: 1, currentA: Math.abs(reading.netW) / (voltageV ?? NOMINAL_VOLTAGE), voltageV, estimated: true }];
}

export function parseConnection(text: string | undefined, source: Connection['source']): Connection | null {
  const m = /^([13])\s*[x×]\s*(\d{2,3})$/i.exec(text?.trim() ?? '');
  if (!m) return null;
  return { phases: m[1] === '3' ? 3 : 1, maxAmps: Number(m[2]), source };
}

// ---- history -----------------------------------------------------------------

const HOUR = 3_600_000;

/** Start of the local calendar day containing `at`. */
export function startOfLocalDay(at: Date): Date {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate());
}

/** The window and bucket size for a history range, in the viewer's local time. */
export function historyWindow(range: HistoryRange, now: Date): { from: Date; to: Date; bucket: 'hour' | 'day' } {
  const today = startOfLocalDay(now);
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (range === 'today') return { from: today, to: tomorrow, bucket: 'hour' };
  const days = range === '7d' ? 7 : 30;
  return {
    from: new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1)),
    to: tomorrow,
    bucket: 'day',
  };
}

/** Every bucket start of the window, in order (calendar-aware, so DST days have 23 or 25 hours). */
export function bucketStarts(from: Date, to: Date, bucket: 'hour' | 'day'): Date[] {
  const out: Date[] = [];
  if (bucket === 'hour') {
    for (let t = from.getTime(); t < to.getTime(); t += HOUR) out.push(new Date(t));
  } else {
    for (let d = new Date(from); d < to; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) out.push(d);
  }
  return out;
}

/** Lays measured buckets onto the full window, so missing data stays a visible hole. */
export function shapeHistory(
  range: HistoryRange,
  now: Date,
  measured: { start: string; importWh: number | null; exportWh: number | null }[],
  peaks: Pick<EnergyHistory, 'peakImport' | 'peakExport' | 'phasePeaks'>,
): EnergyHistory {
  const { from, to, bucket } = historyWindow(range, now);
  const byStart = new Map(measured.map((m) => [new Date(m.start).getTime(), m]));
  const kwh = (wh: number | null | undefined) => (wh == null ? null : wh / 1000);
  const buckets: EnergyBucket[] = bucketStarts(from, to, bucket).map((start) => {
    const m = byStart.get(start.getTime());
    return { start: start.toISOString(), importKwh: kwh(m?.importWh), exportKwh: kwh(m?.exportWh) };
  });
  const sum = (pick: (b: EnergyBucket) => number | null) => buckets.reduce((total, b) => total + (pick(b) ?? 0), 0);
  return {
    range,
    bucket,
    from: from.toISOString(),
    to: to.toISOString(),
    buckets,
    totalImportKwh: sum((b) => b.importKwh),
    totalExportKwh: sum((b) => b.exportKwh),
    ...peaks,
  };
}
